<svelte:window on:resize={computeSizeValues}/>

<label id={labelId} for={id} class="prom-grid-select--label {labelHiddenClass}">
    {label}
</label>

<div
    {id}
    class="prom-grid-select {gridDisplaySizeClass}"
    tabindex="0"
    role="menu"
    aria-labelledby={labelId}
    aria-activedescendant={itemId(id, focusedValue)}
    bind:this={containerElement}
    on:focus={setFocusedDefault}
    on:keydown={keydownHandler}
>
    {#each grid.columns as column}
    <div class="prom-grid-select--column">
        {#each column as item}
        <button
            id={itemId(id, item.value)}
            class="prom-grid-select--item {itemClass(item.value, selectedValue, focusedValue)}"
            tabindex="-1"
            role="menuitemradio"
            lang={item.lang}
            aria-checked={ariaChecked(item.value, selectedValue)}
            data-value={item.value}
            on:click={(e) => selectItemValue(e, item.value)}
            on:mousedown={(e) => mouseDownItemHandler(e, item.value)}
        >
            <span class="prom-grid-select--item-text">{item.text}</span>
        </button>
        {/each}
    </div>
    {/each}
</div>

<button
    bind:this={longestItemElement}
    class="prom-grid-select--item prom-grid-select--longest-item-example"
    tabindex="-1"
    data-value={longestItem.value}
    aria-hidden="true">
    <span class="prom-grid-select--item-text">{longestItem.text}</span>
</button>

<script>
    import { Keys } from '@itslearning/atlas/keyboard/keys';
    import { normalizeKey } from '@itslearning/atlas/keyboard/normalize';
    import { createEventDispatcher , onMount} from 'svelte';

    const dispatch = createEventDispatcher();

    /** @type {string} */
    export let id = 'grid-select';
    /** @type {string} */
    export let label;
    /** @type {{value: number|string, text: string, lang: null|string}} */
    export let items = [];
    /** @type {boolean} */
    export let labelHidden = false;
    /** @type {number|string} */
    export let focusedValue = null;
    /** @type {number|string} */
    export let selectedValue = null;
    /** @type {boolean} */
    export let large = false;
    /** @type {number} */
    export let minColumnWidth = 0;
    /** @type {number} */
    export let containerWidth = 0;

    let containerElement = undefined;
    let longestItemElement = undefined;

    onMount(computeSizeValues);

    // This forces the computed function to run whenever items in the array change
    $: if (items.some(i => i)) {
        if (grid.hasContent && grid.map[focusedValue] === undefined) {
            setFocusedDefault();
        }

        computeSizeValues();
    }

    $: labelHiddenClass = labelHidden ? 'screen-reader' : '';

    $: labelId =  id + '--label';

    $: gridDisplaySizeClass = large ? 'large' : '';

    $: longestItem = ((items) => {
        if (!items || !items.length) {
            return {};
        }

        return items.reduce((previous, current) => {
            return current.text.length > previous.text.length
                ? current
                : previous;
        }, items[0]);
    })(items);

    /**
     * Splits items into columns and maps their values to their coordinates
     * @param {[{value: number|string}]} items Input items array
     * @param {number} itemsPerColumn Max number of items in each column
     * @returns {columns: [[{value: number|string}]],
                map: {value: {x: number, y: number}}, hasContent:bool}
        * Returns an object with the grid's columns containing items, a map
        * of item values to coordinates and hasContent set to true if there
        * are any items
        */
    $: grid = (({items, containerWidth, minColumnWidth}) => {
        const map = {};
        const columns = [];

        let col = [];
        let hasContent = false;

        const numOfColumns = Math.floor(containerWidth / minColumnWidth);
        const itemsPerColumn = Math.ceil(items.length / numOfColumns);

        items.forEach(item => {
            // If there is even one item set hasContent to true
            hasContent = true;
            // Map items to their coordinates so their coords can be looked up by value
            map[item.value] = {x: columns.length, y: col.length};

            // Loop through items adding them to col
            col.push(item);

            // once col is large enough add it to columns and start a new col
            if (col.length === itemsPerColumn) {
                columns.push(col);
                col = [];
            }
        });

        // Add the incomplete col to columns if it has anything in it
        if (col.length > 0) {
            columns.push(col);
        }

        return {columns, map, hasContent};
    })({items, containerWidth, minColumnWidth});

    /**
     * Get the item's aria-checked value
     * @param {number|string} value The value of the item
     * @param {number|string} selectedValue Current selectedValue
     * @returns {string} true or false based if the item is selected
     */
    const ariaChecked = (value, selectedValue) => (value === selectedValue) ? 'true' : 'false';

    /**
     * Gets a unique Id to use as an item's dom id
     * @param {string} gridSelectId the component's id attribute
     * @param {number|string} value The value of the item
     * @returns {string} dom id value
     */
    const itemId = (gridSelectId, itemValue) => gridSelectId + '--item-' + itemValue;

    /**
     * Get dynamic classes for an item
     * @param {number|string} value The value of the item
     * @param {number|string} selectedValue Current selectedValue
     * @param {number|string} focusedValue Current focusedValue
     * @returns {string} of classes based on the state of the item
     */
    function itemClass (value, selectedValue, focusedValue) {
                const extraClasses = [];

                if (value === selectedValue) {
                    extraClasses.push('selected');
                }

                if (value === focusedValue) {
                    extraClasses.push('focused');
                }

                return extraClasses.join(' ');
            }

    /**
     * Focuses the item with focusedValue
     * @param {Event} event The event that triggered the focus
     * @param {string|number} value the value of the item to be focused
     */
    function focusItemValue(event, value) {
        focusedValue = value;
    }

    /**
     * Selects and focuses the item with selectedValue and triggers 'select' event
     * @param {Event} event The event that triggered the select
     * @param {string|number} value the value of the item to be selected
     */
        function selectItemValue(event, value) {
        selectedValue = value;
        focusItemValue(event, value);
        dispatch('select', {event, data: value});
    }

    /**
     * Sets focus to the selectedValue if available or the first item
     */
    function setFocusedDefault() {
        if (!grid.hasContent) {
            return;
        }

        if (grid.map[selectedValue] !== undefined) {
            focusedValue = selectedValue;
        } else {
            focusedValue = grid.columns[0][0].value;
        }
    }

    /**
     * Handles keyboard events, enter, space and and arrow keys are
     * intercepted and used to select and navigate the grid
     * @param {KeyboardEvent} event The keydown event that triggered this method
     */
        function keydownHandler(event) {
        const { key } = event;
        const focused = grid.map[focusedValue];

        if (focused === null || !grid.hasContent) {
            return;
        }

        let {x, y} = focused;

        switch (normalizeKey(key)) {
        // For enter or space call click on the selected item
        // while suppressing the default action
        case Keys.ENTER:
        case Keys.SPACE:
            event.preventDefault();
            selectItemValue(event, grid.columns[x][y].value);

            return;

        // Arrow keys navigate within the grid columns
        case Keys.RIGHT:
            x += 1;

            // If it's past the end of the row wrap around
            if (x >= grid.columns.length || y >= grid.columns[x].length) {
                x = 0;
            }

            break;
        case Keys.LEFT:
            x -= 1;

            // If it's before the start of the row wrap around
            if (x < 0) {
                x = grid.columns.length - 1;

                // If this column doesn't have an item this far down move left
                if (y >= grid.columns[x].length) {
                    x -= 1;
                }
            }

            break;
        case Keys.UP:
            y -= 1;

            // If moved past the column's start shift selection left and to the last item
            if (y < 0) {
                x -= 1;

                // If too far left wrap around
                if (x < 0) {
                    x = grid.columns.length - 1;
                }

                // select the last item in the column
                y = grid.columns[x].length - 1;
            }

            break;
        case Keys.DOWN:
            y += 1;

            // If past the end of the column select first item of column to the right
            if (y >= grid.columns[x].length) {
                x += 1;
                y = 0;

                // If there was no column to the right wrap to first column
                if (x >= grid.columns.length) {
                    x = 0;
                }
            }

            break;
        default:
            return;
        }

        // Stop default event propagation and focus the item at x, y
        event.preventDefault();
        focusItemValue(event, grid.columns[x][y].value);
    }

    /**
    * Sets values used for sizing based on the size of elements on the page
    * Called on create and on resize
    */
    function computeSizeValues() {
        if (longestItemElement && containerElement) {
            containerWidth = containerElement.offsetWidth;
            minColumnWidth = longestItemElement.offsetWidth;
        }
    }

    /**
    * Overrides default mousedown behavior to focus the container in the dom
    * and focus the item clicked using the grid select's internal logic
    * @param {MouseEvent} event The mouse event that triggered this method
    * @param {string|number} itemValue The value of the item that was clicked
    */
    function mouseDownItemHandler(event, itemValue) {
        if (containerElement) {
            containerElement.focus();
        }

        event.preventDefault();
        focusItemValue(event, itemValue);
    }
</script>
